import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", gst_number: "", pan_number: "", notes: "", is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try { const { data } = await api.get("/suppliers?limit=100"); setRows(data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", contact_person: "", phone: "", email: "", gst_number: "", pan_number: "", notes: "", is_active: true }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Supplier updated" : "Supplier created");
      setOpen(false); fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/suppliers/${r.id}`); toast.success("Deleted"); fetchRows(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", header: "Supplier", render: (r) => (
      <div><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.contact_person || "—"}</div></div>
    )},
    { key: "phone", header: "Phone" },
    { key: "email", header: "Email" },
    { key: "gst_number", header: "GSTIN", render: (r) => r.gst_number ? <span className="font-mono text-xs">{r.gst_number}</span> : "—" },
    { key: "is_active", header: "Status", className: "w-24",
      render: (r) => <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-accent hover:bg-accent" : ""}>{r.is_active ? "Active" : "Inactive"}</Badge> },
    { key: "actions", header: "", className: "w-24 text-right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={`edit-sup-${r.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`delete-sup-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
        </div>
      )},
  ];

  return (
    <div className="p-8" data-testid="suppliers-page">
      <PageHeader title="Suppliers" subtitle="Vendor directory"
        actions={<Button onClick={openCreate} className="btn-primary-glow" data-testid="add-sup-btn"><Plus className="w-4 h-4 mr-2" />New supplier</Button>} />
      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openEdit} testId="suppliers-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit supplier" : "New supplier"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" /></div>
            <div><Label>Contact person</Label><Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gst_number || ""} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={form.pan_number || ""} onChange={(e) => setForm({ ...form, pan_number: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="btn-primary-glow" data-testid="save-sup-btn">{saving ? "Saving…" : editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
